import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Radio,
  RadioGroup,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
} from '@mui/joy';
import {
  House as HomeIcon,
  Wrench as ServiceIcon,
  Phone as ContactIcon,
  Article as GenericIcon,
  MapPin as LocationIcon,
  Megaphone as LandingIcon,
  Images as GalleryIcon,
} from '@phosphor-icons/react';

interface Template {
  key: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  preview?: string;
  features: string[];
  bestFor: string;
}

interface TemplateSelectorProps {
  open?: boolean;
  onClose?: () => void;
  currentTheme?: string;
  onThemeSelect?: (themeKey: string) => void;
  value?: string;
  onChange?: (value: string) => void;
  allowedTemplates?: string[];
  mode?: 'templates' | 'themes'; // Add mode to switch between templates and themes
}

const THEMES: Template[] = [
  {
    key: 'atlas',
    name: 'Atlas Professional',
    description: 'Clean, modern design perfect for established contractors',
    icon: <HomeIcon />,
    features: ['Professional', 'Clean Layout', 'Trust Building', 'Mobile Optimized'],
    bestFor: 'Established contractors looking for a professional presence',
  },
  {
    key: 'bold',
    name: 'Bold Impact',
    description: 'Strong visual presence for companies that stand out',
    icon: <HomeIcon />,
    features: ['Eye-catching', 'High Contrast', 'Modern Design', 'Strong CTAs'],
    bestFor: 'Companies wanting to make a strong first impression',
  },
  {
    key: 'classic',
    name: 'Classic Trust',
    description: 'Traditional design that builds confidence',
    icon: <HomeIcon />,
    features: ['Traditional', 'Trustworthy', 'Time-tested', 'Clear Navigation'],
    bestFor: 'Businesses emphasizing reliability and trust',
  },
  {
    key: 'modern',
    name: 'Modern Edge',
    description: 'Cutting-edge design for innovative contractors',
    icon: <HomeIcon />,
    features: ['Innovative', 'Latest Trends', 'Interactive', 'Tech-forward'],
    bestFor: 'Forward-thinking contractors embracing innovation',
  },
];

const TEMPLATES: Template[] = [
  {
    key: 'home',
    name: 'Homepage',
    description: 'Your main landing page with hero, services, testimonials',
    icon: <HomeIcon />,
    features: ['Hero Section', 'Service Grid', 'Testimonials', 'Call-to-Action'],
    bestFor: 'Main entry point for your website',
  },
  {
    key: 'service',
    name: 'Service Page',
    description: 'Detailed information about a specific service',
    icon: <ServiceIcon />,
    features: ['Service Details', 'Pricing Info', 'Process Steps', 'FAQ Section'],
    bestFor: 'Individual service pages like "Roof Repair" or "Gutter Installation"',
  },
  {
    key: 'contact',
    name: 'Contact Page',
    description: 'Contact forms, map, and business information',
    icon: <ContactIcon />,
    features: ['Contact Form', 'Google Map', 'Business Hours', 'Multiple Locations'],
    bestFor: 'Making it easy for customers to reach you',
  },
  {
    key: 'generic',
    name: 'General Page',
    description: 'Flexible layout for any type of content',
    icon: <GenericIcon />,
    features: ['Flexible Sections', 'Rich Content', 'Media Support', 'Custom Layout'],
    bestFor: 'About Us, Privacy Policy, or any custom content',
  },
  {
    key: 'location',
    name: 'Location Page',
    description: 'Optimized for local SEO and service areas',
    icon: <LocationIcon />,
    features: ['Area Map', 'Local Services', 'Area Statistics', 'Local Testimonials'],
    bestFor: 'City or neighborhood-specific pages for local SEO',
  },
  {
    key: 'landing',
    name: 'Landing Page',
    description: 'High-converting page for campaigns and offers',
    icon: <LandingIcon />,
    features: ['Hero with Offer', 'Benefits List', 'Social Proof', 'Urgency Elements'],
    bestFor: 'Special promotions, seasonal offers, or PPC campaigns',
  },
  {
    key: 'gallery',
    name: 'Project Gallery',
    description: 'Showcase your best work with before/after photos',
    icon: <GalleryIcon />,
    features: ['Photo Grid', 'Before/After', 'Project Details', 'Filter by Type'],
    bestFor: 'Building trust by showing completed projects',
  },
];

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  open = false,
  onClose,
  value = '',
  onChange,
  onThemeSelect,
  currentTheme,
  allowedTemplates,
  mode = 'templates',
}) => {
  // Use THEMES or TEMPLATES based on mode
  const sourceItems = mode === 'themes' ? THEMES : TEMPLATES;
  
  const availableTemplates = allowedTemplates
    ? sourceItems.filter(t => allowedTemplates.includes(t.key))
    : sourceItems;

  // If used as a modal
  if (open !== undefined) {
    return (
      <Modal open={open} onClose={onClose || (() => {})}>
        <ModalDialog size="lg">
          <DialogTitle>
            {mode === 'themes' ? 'Choose a Website Theme' : 'Choose a Page Template'}
          </DialogTitle>
          <DialogContent>
            <RadioGroup
              value={value || currentTheme || ''}
              onChange={(event) => {
                if (onChange) onChange(event.target.value);
                if (onThemeSelect) onThemeSelect(event.target.value);
              }}
            >
              <Grid container spacing={2}>
                {availableTemplates.map((template) => (
                  <Grid xs={12} md={6} key={template.key}>
                    <Card
                      variant={(value || currentTheme) === template.key ? 'solid' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: 'md',
                        },
                      }}
                      onClick={() => {
                        if (onChange) onChange(template.key);
                        if (onThemeSelect) onThemeSelect(template.key);
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Radio value={template.key} />
                          
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 'sm',
                              bgcolor: 'neutral.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {template.icon}
                          </Box>
                          
                          <Box sx={{ flex: 1 }}>
                            <Typography level="title-md">
                              {template.name}
                            </Typography>
                            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                              {template.description}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {template.features.map((feature, idx) => (
                                <Chip key={idx} size="sm" variant="soft">
                                  {feature}
                                </Chip>
                              ))}
                            </Box>
                            
                            <Typography level="body-xs" sx={{ mt: 1, color: 'var(--brand-text, #7013f5)' }}>
                              {template.bestFor}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
          </DialogContent>
        </ModalDialog>
      </Modal>
    );
  }

  // Inline version (not used currently)
  return (
    <Box>
      <Typography level="h4" sx={{ mb: 3 }}>
        {mode === 'themes' ? 'Choose a Website Theme' : 'Choose a Page Template'}
      </Typography>
      
      <RadioGroup
        value={value || currentTheme || ''}
        onChange={(event) => {
          if (onChange) onChange(event.target.value);
          if (onThemeSelect) onThemeSelect(event.target.value);
        }}
      >
        <Grid container spacing={2}>
          {availableTemplates.map((template) => (
            <Grid xs={12} md={6} key={template.key}>
              <Card
                variant={value === template.key ? 'solid' : 'outlined'}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 'md',
                  },
                }}
                onClick={() => {
                  if (onChange) onChange(template.key);
                  if (onThemeSelect) onThemeSelect(template.key);
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Radio value={template.key} />
                    
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 'sm',
                        bgcolor: value === template.key ? 'primary.100' : 'neutral.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: value === template.key ? 'primary.600' : 'neutral.600',
                      }}
                    >
                      {template.icon}
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography level="title-md" sx={{ mb: 0.5 }}>
                        {template.name}
                      </Typography>
                      <Typography level="body-sm" sx={{ mb: 2 }}>
                        {template.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {template.features.map((feature, idx) => (
                          <Chip
                            key={idx}
                            size="sm"
                            variant="soft"
                            color={value === template.key ? 'primary' : 'neutral'}
                          >
                            {feature}
                          </Chip>
                        ))}
                      </Box>
                      
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        <strong>Best for:</strong> {template.bestFor}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </RadioGroup>
    </Box>
  );
};
